// @flow

import React, { useState, useRef, useCallback } from 'react';
import styled, { type StyledComponent } from 'styled-components';
import lazySizes from 'lazysizes';
import { mediaBreakpoints } from '../../../styles/GlobalStyle';
import { useDeepCompareEffect } from '../hooks/useDeepCompareEffect';

type PictureSource = {|
  breakpoint: $Keys<typeof mediaBreakpoints>,
  src: string,
  default?: boolean,
|};

type PictureProps = {|
  className?: string,
  imgClassName?: string,
  alt?: string,
  sources: PictureSource[],
|};

const RawPicture = ({
  className,
  imgClassName,
  alt,
  sources,
}: PictureProps) => {
  const imgNode = useRef(null);
  const [defaultSource, setDefaultSource] = useState('');

  const buildMediaQuery = useCallback((source: PictureSource) => {
    const mediaBreakpoint = mediaBreakpoints[source.breakpoint];

    return `(min-width: ${mediaBreakpoint})`;
  }, []);

  useDeepCompareEffect(() => {
    let source = sources.find(s => s.default);
    if (!source) {
      [source] = sources;
    }
    setDefaultSource(source.src);

    if (imgNode.current) {
      if (!lazySizes.hC(imgNode.current, 'lazyload')) {
        lazySizes.aC(imgNode.current, 'lazyload');
      } else if (lazySizes.hC(imgNode.current, 'lazyloaded')) {
        lazySizes.rC(imgNode.current, 'lazyloaded');
      }
    }
  }, [sources]);

  return (
    <picture className={className}>
      {sources.map(source => (
        <source
          data-srcset={source.src}
          media={buildMediaQuery(source)}
          key={source.src}
        />
      ))}
      <img
        className={imgClassName}
        src="data:image/gif;base64,R0lGODlhAQABAAAAACH5BAEKAAEALAAAAAABAAEAAAICTAEAOw=="
        data-src={defaultSource}
        alt={alt}
        ref={imgNode}
      />
    </picture>
  );
};

export const Picture: StyledComponent<
  PictureProps,
  {},
  typeof RawPicture
> = styled(RawPicture)`
  .lazyload,
  .lazyloading {
    opacity: 0;
  }
  .lazyloaded {
    opacity: 1;
    transition: opacity 300ms;
  }
`;
